import {
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-currency-action-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './currency-action-input.component.html',
  styleUrls: ['./currency-action-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyActionInputComponent),
      multi: true,
    },
  ],
})
export class CurrencyActionInputComponent implements ControlValueAccessor {
  /** UI Inputs (UNCHANGED) */
  @Input() allowNull = false;
  @Input() buttonText = 'Sent';
  @Input() actionDisabled = true;
  @Input() inputDisabled = false;
  @Input() inputReadonly = false;
  @Input() useNumberInput = true;
  @Input() showAction = true;
  @Input() allowDecimal = false;
  @Input() placeholder = '';
  @Input() currencyPrefix = '$';
  /** When false, hides the prefix (e.g. percentage fields that use a suffix instead). */
  @Input() showPrefix = true;
  /** Optional suffix affix shown after the input (e.g. "%"). */
  @Input() suffix = '';
  @Input() minInput = '0';
  /** Upper bound for numeric input; values above this are clamped on input. */
  @Input() maxInput: string | null = null;
  /** `compact` matches 38px form fields (e.g. Create Discount modal). */
  @Input() size: 'default' | 'compact' = 'default';
  @Input() inputId = '';

  @Output() valueChange = new EventEmitter<number | null>();
  @Output() action = new EventEmitter<void>();

  /** Internal value */
  private _value: number | null = null;

  @Input()
  set value(val: number | null) {
    this._value = val;
    this.cdr.markForCheck();
  }

  get value(): number | null {
    return this._value;
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(value: number | null): void {
    this._value = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.inputDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  get displayValue(): string {
    if (this._value === null || this._value === undefined) {
      return '';
    }
    return this.allowDecimal ? String(this._value) : String(this._value);
  }

  get inputStep(): string {
    return this.allowDecimal ? '0.01' : '1';
  }

  get effectiveReadonly(): boolean {
    return this.inputReadonly || this.inputDisabled;
  }

  get effectiveDisabled(): boolean {
    return this.inputDisabled;
  }

  /** FIXED: single source of truth */
  onInput(event: Event): void {
    if (this.effectiveReadonly) return;

    const input = event.target as HTMLInputElement;
    const raw = input.value.trim().replace(/,/g, '');

    let newValue: number | null = null;
    let display = '';

    if (raw === '') {
      newValue = null;
      display = '';
    } else if (this.allowDecimal) {
      // Keep at most 2 decimal places while typing (e.g. 12.34); allow trailing "."
      const match = raw.match(/^(\d*)(\.?)(\d{0,2})/);
      display = match ? `${match[1]}${match[2]}${match[3]}` : '';

      if (display === '' || display === '.') {
        newValue = null;
      } else {
        const n = parseFloat(display);
        newValue = Number.isNaN(n) || n < 0 ? null : Math.round(n * 100) / 100;
      }
    } else {
      // Integers only (e.g. percentage 0–100)
      display = raw.replace(/[^\d]/g, '');
      const n = Number(display);
      newValue = display === '' || Number.isNaN(n) || n < 0 ? null : Math.trunc(n);
      display = newValue === null ? '' : String(newValue);
    }

    if (newValue !== null && this.maxInput !== null && this.maxInput !== '') {
      const max = Number(this.maxInput);
      if (!Number.isNaN(max) && newValue > max) {
        newValue = max;
        display = this.allowDecimal ? String(newValue) : String(Math.trunc(newValue));
      }
    }

    this._value = newValue;
    // Preserve in-progress decimal typing (e.g. "12.") while clamping DOM to sanitized text
    input.value = display;
    this.valueChange.emit(this._value);
    this.onChange(this._value);
  }

  onBlur(): void {
    this.onTouched();
  }

  get isBelowMin(): boolean {
    if (this._value === null || this._value === undefined) {
      return true;
    }

    return this._value < Number(this.minInput);
  }

  onActionClick(): void {
    if (!this.actionDisabled && !this.isBelowMin) {
      this.action.emit();
    }
  }

  blockInvalidKeys(event: KeyboardEvent): void {
    if (event.key === '-' || event.key === 'e' || event.key === 'E' || event.key === '+') {
      event.preventDefault();
      return;
    }
    // Percentage / integer mode: no decimal point
    if (!this.allowDecimal && (event.key === '.' || event.key === ',')) {
      event.preventDefault();
    }
  }
}
